import { t, jt } from 'ttag'
import styled from 'styled-components'
import { useState, useMemo, useCallback, useContext } from 'react'
import { useMutation } from 'react-query'
import { useDispatch } from 'react-redux'
import isEmail from 'validator/lib/isEmail'
import PropTypes from 'prop-types'

import { api } from '@h2/api'
import { useForm } from '@h2/common/components/hooks/useForm'
import { useNavigator } from '@h2/common/components/hooks/useNavigator'
import { Link as LinkBase } from '@h2/common/components/link'
import { userForgotCredential } from '@h2/common/actions/userForgotCredential'
import { Text } from '@h2/components/typography/text'
import { Alert } from '@h2/components/display/alert'
import { Field } from '@h2/components/inputs/field'
import { TextInput } from '@h2/components/inputs/text'
import { Button } from '@h2/components/buttons/button'
import { Flex } from '@h2/components/layout/flex'
import { Globals } from '@h2/globals'
import { Back } from '@h2/common/components/registration/back'
import { features } from '@h2/components/media'

const NavianceText = styled(Text)`
text-align: center;
font-size: 14px;
padding: 0px 40px;
`
const ButtonLink = styled(Button)`
width: 235px;
height: 38px;
margin-left: auto;
margin-top: 35px;
@media (${features.maxWidth.laptop}) {
  margin-left: inherit;
}
`
const FlexDirection = styled(Flex)`
margin-top: 100.61px;
@media (${features.maxWidth.laptop}) {
  flex-direction: column;
  align-items: center;
}
@media (${features.maxWidth.widescreen}) {
  margin-top: 50px;
}
`
const Form = ({ onClickLink }) => {
  const dispatch = useDispatch()
  const navigate = useNavigator()
  const [response, setResponse] = useState(null)
  const { register, handleOnSubmit, error, isInvalid } = useForm({
    email(form) {
      if (!form.email?.trim()) {
        return t`Username is required`
      }

      if (!isEmail(form.email.trim())) {
        return t`Invalid Email address`
      }
    }
  })
  const { mutate, isLoading } = useMutation(
    body =>
      api.fetch('/user/request/usernamelookup', {
        version: 'v1',
        method: 'POST',
        body: JSON.stringify(body)
      }),
    {
      onSuccess: data => {
        if (data.success) {
          setResponse({ type: 'success', message: data.success })
        }

        if (data.error) {
          setResponse({ type: 'error', message: data.error })
        }
      },
      onError: () => {
        setResponse({
          type: 'error',
          message: t`An error occured, please try again later.`
        })
      }
    }
  )
  const link = useMemo(
    () => (
      <LinkBase
        key="jt-key"
        href="/resetpassword"
        onClick={evt => {
          evt.preventDefault()

          if (typeof onClickLink === 'function') {
            onClickLink(evt)
          } else {
            navigate(evt.target.pathname)
          }
        }}>
        {t`reset your password here`}
      </LinkBase>
    ),
    [onClickLink, navigate]
  )
  const onClickCancel = useCallback(() => {
    if (typeof onClickLink === 'function') {
      dispatch(userForgotCredential(''))
    } else {
      navigate('/signin')
    }
  }, [onClickLink, dispatch, navigate])
  const { isNavianceUser } = useContext(Globals)

  return (
    <form onSubmit={handleOnSubmit(form => mutate(form))}>
      {isNavianceUser ? (
        <NavianceText>
          {t`Enter the email address you used when registering for an account, then click Recover. An email will be sent to that address with your username.`}
        </NavianceText>
      ) : (
        <Text>
          {jt`If you can't remember the username you used when you registered for an account, you can look it up, but only if you supplied your email address. In the box below, enter the e-mail address you provided and click "Recover". An email will then be sent to that address with your username. If you know your username, ${link}.`}
        </Text>
      )}
        {response && <Alert {...response} onClose={() => setResponse(null)} />}
        <Field
          isRequired
          label={t`Email Address`}
          status={error.email ? 'error' : 'default'}
          desc={error.email}
          margin="20px 0">
          <TextInput {...register('email', { required: true })} />
        </Field>
      {!isNavianceUser ? (
        <Flex gap="15px">
          <Button
            type="button"
            size="large"
            variant="outlined"
            isDisabled={isLoading}
            onClick={onClickCancel}>
            {t`Cancel`}
          </Button>
          <Button
            type="submit"
            size="large"
            isDisabled={isInvalid || isLoading}
            isLoading={isLoading}>
            {t`Recover`}
          </Button>
        </Flex>
      ) : (
        <FlexDirection>
          <Back onClick={onClickCancel} />
          <ButtonLink
            type="submit"
            size="large"
            align="center"
            isDisabled={isInvalid || isLoading}
            isLoading={isLoading}>
            {t`Recover`}
          </ButtonLink>
        </FlexDirection>
      )}
    </form>
  )
}

Form.propTypes = {
  /** Callback when clicking link within text. Only defined when the form is rendered inside a modal dialog. */
  onClickLink: PropTypes.func
}

export { Form }
